import { useState, useEffect, useCallback } from "react";
import { apiFetch, ApiError } from "@/lib/api-client";

export interface WorkflowNode {
  id: string;
  type: string;
  config: Record<string, unknown>;
  position?: { x: number; y: number };
}

export interface WorkflowEdge {
  id: string;
  source: string;
  target: string;
  label?: string;
  condition?: string;
}

export interface WorkflowComplianceCheck {
  key: "consent" | "suppression" | "rate_limit" | "opt_out";
  label: string;
  state: "pass" | "warn";
  message: string;
}

export interface WorkflowComplianceSummary {
  status: "green" | "yellow" | "red";
  checks: WorkflowComplianceCheck[];
}

export interface WorkflowQuietHours {
  enabled: boolean;
  start: string;
  end: string;
  timezoneSource: string;
  fallbackTimezone: string;
}

export interface WorkflowFrequencyCap {
  maxMessages: number;
  periodHours: number;
  appliesTo: "marketing" | "transactional" | "all";
}

export interface WorkflowSummary {
  id: string;
  name: string;
  status: string;
  currentVersion: number | null;
  compliance?: WorkflowComplianceSummary | null;
  triggers: Array<{
    type: string;
    eventType?: string;
    config?: Record<string, unknown>;
  }>;
  createdAt: string;
  updatedAt: string;
}

export interface WorkflowDetail extends WorkflowSummary {
  settings: Record<string, unknown> | null;
  reentryPolicy: string;
  quietHours: WorkflowQuietHours | null;
  frequencyCap: WorkflowFrequencyCap | null;
}

export interface WorkflowVersion {
  id: string;
  version: number;
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
  publishedAt: string;
  publishedBy?: string | null;
  changeSummary?: string | null;
  nodeCount?: number;
  edgeCount?: number;
  isCurrent?: boolean;
}

export interface WorkflowTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  vertical: string;
  triggerType: string;
  stepCount: number;
}

export interface WorkflowExportPackage {
  schemaVersion: number;
  exportedAt?: string;
  source?: Record<string, unknown>;
  workflow: {
    name: string;
    triggers?: Array<{
      type: string;
      eventType?: string;
      config?: Record<string, unknown>;
    }>;
    settings?: Record<string, unknown> | null;
    reentryPolicy?: string;
    quietHours?: WorkflowQuietHours | null;
    frequencyCap?: WorkflowFrequencyCap | null;
  };
  version: {
    nodes: WorkflowNode[];
    edges: WorkflowEdge[];
  };
}

export function useWorkflowList(status?: string, triggerType?: string) {
  const [workflows, setWorkflows] = useState<WorkflowSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    const search = new URLSearchParams();
    if (status) search.set("status", status);
    if (triggerType) search.set("trigger_type", triggerType);
    const params = search.size > 0 ? `?${search.toString()}` : "";
    try {
      const res = await apiFetch<{ data: WorkflowSummary[] }>(`/crm/workflows${params}`);
      setWorkflows(res.data ?? []);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to load workflows");
    } finally {
      setIsLoading(false);
    }
  }, [status, triggerType]);

  useEffect(() => { fetch(); }, [fetch]);
  return { workflows, isLoading, error, refetch: fetch };
}

export function useWorkflow(id: string | null) {
  const [workflow, setWorkflow] = useState<WorkflowDetail | null>(null);
  const [version, setVersion] = useState<WorkflowVersion | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!id) { setWorkflow(null); setVersion(null); return; }
    setIsLoading(true);

    Promise.all([
      apiFetch<{ data: WorkflowDetail }>(`/crm/workflows/${id}`),
      apiFetch<{ version: WorkflowVersion }>(`/crm/workflows/${id}/versions/latest`).catch(() => ({ version: null })),
    ]).then(([wRes, vRes]) => {
      setWorkflow(wRes.data);
      setVersion(vRes.version);
    }).finally(() => setIsLoading(false));
  }, [id]);

  return { workflow, version, isLoading };
}

export function useWorkflowTemplates() {
  const [templates, setTemplates] = useState<WorkflowTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await apiFetch<{ data: WorkflowTemplate[] }>("/crm/workflows/templates");
      setTemplates(res.data ?? []);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to load workflow templates");
      setTemplates([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);
  return { templates, isLoading, error, refetch: fetch };
}

export function useWorkflowVersions(workflowId: string | null, enabled = true) {
  const [versions, setVersions] = useState<WorkflowVersion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    if (!workflowId || !enabled) {
      setVersions([]);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const res = await apiFetch<{ data: WorkflowVersion[] }>(`/crm/workflows/${workflowId}/versions`);
      setVersions(res.data ?? []);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to load workflow versions");
      setVersions([]);
    } finally {
      setIsLoading(false);
    }
  }, [workflowId, enabled]);

  useEffect(() => { void fetch(); }, [fetch]);
  return { versions, isLoading, error, refetch: fetch };
}

export async function createWorkflow(data: {
  name: string;
  triggers?: Array<{
    type: string;
    eventType?: string;
    config?: Record<string, unknown>;
  }>;
}): Promise<WorkflowSummary> {
  const res = await apiFetch<{ workflow: WorkflowSummary }>("/crm/workflows", {
    method: "POST",
    body: JSON.stringify(data),
  });
  return res.workflow;
}

export async function createWorkflowFromTemplate(templateId: string, name?: string): Promise<{ workflowId: string; versionId: string }> {
  const res = await apiFetch<{ data: { workflowId: string; versionId: string } }>("/crm/workflows/from-template", {
    method: "POST",
    body: JSON.stringify({ templateId, name }),
  });
  return res.data;
}

export async function exportWorkflowPackage(workflowId: string): Promise<WorkflowExportPackage> {
  const res = await apiFetch<{ data: WorkflowExportPackage }>(`/crm/workflows/${workflowId}/export`);
  return res.data;
}

export async function importWorkflowPackage(
  workflowPackage: WorkflowExportPackage,
  name?: string,
): Promise<{ workflowId: string; versionId: string }> {
  const res = await apiFetch<{ data: { workflowId: string; versionId: string } }>("/crm/workflows/import", {
    method: "POST",
    body: JSON.stringify({ package: workflowPackage, name }),
  });
  return res.data;
}

export async function publishVersion(workflowId: string, nodes: WorkflowNode[], edges: WorkflowEdge[]): Promise<void> {
  await apiFetch(`/crm/workflows/${workflowId}/versions`, {
    method: "POST",
    body: JSON.stringify({ nodes, edges }),
  });
}

export async function activateWorkflow(workflowId: string): Promise<void> {
  await apiFetch(`/crm/workflows/${workflowId}`, {
    method: "PATCH",
    body: JSON.stringify({ status: "active" }),
  });
}

export async function pauseWorkflow(workflowId: string): Promise<void> {
  await apiFetch(`/crm/workflows/${workflowId}`, {
    method: "PATCH",
    body: JSON.stringify({ status: "paused" }),
  });
}

export async function restoreWorkflowVersion(workflowId: string, versionId: string): Promise<void> {
  await apiFetch(`/crm/workflows/${workflowId}/versions/${versionId}/restore`, {
    method: "POST",
  });
}
